import { useState, useCallback } from "react";
import { FileUp, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";

interface CVUploadZoneProps {
  onFileSelect?: (file: File) => void;
  onFileChange?: (file: File | null) => void;
  selectedFile?: File | null;
  isLoading?: boolean;
  disabled?: boolean;
  progress?: number;
  progressStatus?: string;
}

export const CVUploadZone = ({ 
  onFileSelect, 
  onFileChange,
  selectedFile,
  isLoading = false, 
  disabled = false,
  progress = 0, 
  progressStatus = "" 
}: CVUploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();
  
  // Determine if component is in loading/disabled state
  const isDisabled = isLoading || disabled;
  
  // Handle file selection - call both callbacks if provided
  const handleFileSelection = useCallback((file: File) => {
    if (onFileSelect) onFileSelect(file);
    if (onFileChange) onFileChange(file);
  }, [onFileSelect, onFileChange]);
  
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isDisabled)
      return;
    setIsDragging(true);
  }, [isDisabled]);
  
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isDisabled)
      return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: t("candidates.cv.invalidFileTypeTitle"),
          description: t("candidates.cv.invalidFileTypeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t("candidates.cv.fileTooLargeTitle"),
          description: t("candidates.cv.fileTooLargeDesc"),
          variant: "destructive",
        });
        return;
      }      handleFileSelection(file);
    }
  }, [handleFileSelection, isDisabled, t]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: t("candidates.cv.invalidFileTypeTitle"),
          description: t("candidates.cv.invalidFileTypeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t("candidates.cv.fileTooLargeTitle"),
          description: t("candidates.cv.fileTooLargeDesc"),
          variant: "destructive",
        });
        return;
      }      handleFileSelection(file);
      e.target.value = ''; // Reset file input
    }
  };
    return (
    <div 
      className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors h-full min-h-[300px] flex flex-col items-center justify-center ${
        isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
      } ${disabled && !isLoading ? 'opacity-60 pointer-events-none' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isLoading ? (
        <div className="w-full max-w-md">
          <h3 className="text-lg font-medium mb-4 dark:text-gray-100">{t("candidates.cv.processingCV")}</h3>
          <Progress value={progress} className="h-2 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{progressStatus || t("candidates.cv.pleaseWait")}</p>
        </div>
      ) : selectedFile ? (
        <div className="flex flex-col items-center">
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-sm p-4 mb-4 w-full max-w-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileUp className="h-8 w-8 text-green-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-900 dark:text-green-100 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-green-600">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (onFileChange) onFileChange(null);
                }}
                disabled={isDisabled}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </Button>
            </div>
          </div>
          <p className="text-sm text-green-600 mb-4">
            {t("candidates.cv.clickToView", { filename: selectedFile.name })}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2"
            disabled={isDisabled}
            onClick={() => document.getElementById('cv-upload')?.click()}
            type="button"
          >
            <Upload className="h-4 w-4" />
            <span>Replace file</span>
          </Button>
          <input 
            id="cv-upload"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="hidden"
            disabled={isDisabled}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <Upload className="h-16 w-16 text-gray-400 dark:text-gray-500 mb-6" />
          <p className="text-lg font-medium mb-3 dark:text-gray-100">{t("candidates.cv.dragAndDrop")}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t("candidates.cv.or")}</p>
          
          <Button 
            variant="outline" 
            size="lg" 
            className="flex items-center gap-2 mb-6"
            disabled={isDisabled}
            onClick={() => document.getElementById('cv-upload')?.click()}
            type="button"
          >
            <FileUp className="h-5 w-5" />
            <span>{t("candidates.cv.browseFiles")}</span>
          </Button>
          <input 
            id="cv-upload"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="hidden"
            disabled={isDisabled}
          />
          
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("candidates.cv.supportedFormats")}
          </p>
        </div>
      )}
    </div>
  );
};
